/**
 * Visit Hooks
 * React Query hooks for visit data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { visitsApi } from '@/api'
import type {
  VisitCreate,
  VisitUpdate,
  VisitFilters,
  VisitClassificationCreate,
  VisitTransactionCreate,
  VisitTransactionUpdate,
} from '@/types/visit'
import type { PaginationParams } from '@/types/common'

// Query keys
export const visitKeys = {
  all: ['visits'] as const,
  lists: () => [...visitKeys.all, 'list'] as const,
  list: (filters?: VisitFilters, pagination?: PaginationParams) =>
    [...visitKeys.lists(), { filters, pagination }] as const,
  details: () => [...visitKeys.all, 'detail'] as const,
  detail: (id: string) => [...visitKeys.details(), id] as const,
  clientVisits: (clientId: string) => [...visitKeys.all, 'client', clientId] as const,
  classifications: () => [...visitKeys.all, 'classifications'] as const,
  classification: (visitId: string) => [...visitKeys.classifications(), visitId] as const,
  transactions: () => [...visitKeys.all, 'transactions'] as const,
  visitTransactions: (visitId: string) => [...visitKeys.transactions(), visitId] as const,
}

/**
 * Get paginated list of visits
 */
export function useVisits(
  filters?: VisitFilters,
  pagination?: PaginationParams
) {
  return useQuery({
    queryKey: visitKeys.list(filters, pagination),
    queryFn: () => visitsApi.getAll(filters, pagination),
  })
}

/**
 * Get single visit by ID
 */
export function useVisit(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: visitKeys.detail(id),
    queryFn: () => visitsApi.getById(id),
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
  })
}

/**
 * Get all visits for a specific client
 * Note: Use useClientVisits from useClients hook for client detail pages
 */
export function useVisitsByClient(clientId: string) {
  return useQuery({
    queryKey: visitKeys.clientVisits(clientId),
    queryFn: () => visitsApi.getClientVisits(clientId),
    enabled: !!clientId,
  })
}

/**
 * Create new visit
 */
export function useCreateVisit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: VisitCreate) => visitsApi.create(data),
    onSuccess: () => {
      // Invalidate visits list to refetch
      queryClient.invalidateQueries({ queryKey: visitKeys.lists() })
    },
  })
}

/**
 * Update existing visit
 */
export function useUpdateVisit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VisitUpdate }) =>
      visitsApi.update(id, data),
    onSuccess: (updatedVisit) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: visitKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: visitKeys.detail(updatedVisit.id),
      })
    },
  })
}

/**
 * Delete visit
 */
export function useDeleteVisit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => visitsApi.delete(id),
    onSuccess: () => {
      // Invalidate visits list
      queryClient.invalidateQueries({ queryKey: visitKeys.lists() })
    },
  })
}

/**
 * Create or update visit classification
 */
export function useCreateVisitClassification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: VisitClassificationCreate) => visitsApi.createClassification(data),
    onSuccess: (classification) => {
      // Invalidate classification for this visit
      queryClient.invalidateQueries({
        queryKey: visitKeys.classification(classification.visit_id),
      })
    },
  })
}

/**
 * Get visit classification
 */
export function useVisitClassification(visitId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: visitKeys.classification(visitId),
    queryFn: () => visitsApi.getClassification(visitId),
    enabled: options?.enabled !== undefined ? options.enabled : !!visitId,
  })
}

/**
 * Create visit transaction
 */
export function useCreateVisitTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: VisitTransactionCreate) => visitsApi.createTransaction(data),
    onSuccess: (transaction) => {
      // Invalidate transactions for this visit
      queryClient.invalidateQueries({
        queryKey: visitKeys.visitTransactions(transaction.visit_id),
      })
    },
  })
}

/**
 * Get all transactions for a visit
 */
export function useVisitTransactions(visitId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: visitKeys.visitTransactions(visitId),
    queryFn: () => visitsApi.getTransactions(visitId),
    enabled: options?.enabled !== undefined ? options.enabled : !!visitId,
  })
}

/**
 * Update an existing visit transaction (assistance details)
 */
export function useUpdateVisitTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VisitTransactionUpdate }) =>
      visitsApi.updateTransaction(id, data),
    onSuccess: (transaction) => {
      queryClient.invalidateQueries({
        queryKey: visitKeys.visitTransactions(transaction.visit_id),
      })
      queryClient.invalidateQueries({ queryKey: visitKeys.detail(transaction.visit_id) })
    },
  })
}
